import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AIRecommendationsModal = ({ isOpen, onClose, analysis, isLoading }) => {
  if (!isOpen) return null;

  const priorityColors = {
    high: 'text-red-600 bg-red-50 border-red-200',
    medium: 'text-yellow-600 bg-yellow-50 border-yellow-200',
    low: 'text-blue-600 bg-blue-50 border-blue-200'
  };

  const categoryIcons = {
    engineers: 'Users',
    materials: 'Package',
    licenses: 'Key',
    configuration: 'Settings'
  };

  const riskColors = {
    low: 'text-success bg-success/10 border-success/30',
    medium: 'text-yellow-600 bg-yellow-50 border-yellow-200',
    high: 'text-red-600 bg-red-50 border-red-200'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-4xl w-full my-8 animate-scale-in">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-primary to-accent shadow-lg">
              <Icon name="Sparkles" size={20} color="white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">AI Cost Analysis</h3>
              <p className="text-xs text-muted-foreground">Powered by OpenAI GPT-5</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
              <p className="text-sm text-muted-foreground">Analyzing your estimate...</p>
            </div>
          ) : analysis ? (
            <>
              {/* Overall Assessment */}
              <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/30 rounded-xl p-5">
                <div className="flex items-start gap-3 mb-3">
                  <Icon name="TrendingDown" size={20} color="var(--color-primary)" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-foreground mb-2">Overall Assessment</h4>
                    <p className="text-sm text-muted-foreground mb-3">{analysis?.overallAssessment?.summary}</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="bg-card/50 rounded-lg p-3 border border-border">
                        <div className="text-xs text-muted-foreground mb-1">Potential Savings</div>
                        <div className="text-lg font-bold text-success">{analysis?.overallAssessment?.totalPotentialSavings}</div>
                      </div>
                      <div className="bg-card/50 rounded-lg p-3 border border-border">
                        <div className="text-xs text-muted-foreground mb-1">Risk Level</div>
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${riskColors?.[analysis?.overallAssessment?.riskLevel]}`}>
                          <Icon name="AlertCircle" size={12} />
                          {analysis?.overallAssessment?.riskLevel?.toUpperCase()}
                        </div>
                      </div>
                      <div className="bg-card/50 rounded-lg p-3 border border-border">
                        <div className="text-xs text-muted-foreground mb-1">Confidence</div>
                        <div className="text-sm font-semibold text-foreground capitalize">{analysis?.overallAssessment?.confidence}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cost-Saving Strategies */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="DollarSign" size={18} color="var(--color-primary)" />
                  <h4 className="text-sm font-semibold text-foreground">Cost-Saving Strategies</h4>
                </div>
                <div className="space-y-3">
                  {analysis?.costSavingStrategies?.map((strategy, idx) => (
                    <div key={idx} className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <h5 className="text-sm font-semibold text-foreground flex-1">{strategy?.title}</h5>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${priorityColors?.[strategy?.priority]}`}>
                          {strategy?.priority?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{strategy?.description}</p>
                      <div className="flex items-center gap-2 text-xs">
                        <Icon name="TrendingDown" size={14} color="var(--color-success)" />
                        <span className="font-medium text-success">Est. Savings: {strategy?.estimatedSavings}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Delivery Mode Alternatives */}
              {analysis?.deliveryModeAlternatives?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="Truck" size={18} color="var(--color-primary)" />
                    <h4 className="text-sm font-semibold text-foreground">Alternative Delivery Modes</h4>
                  </div>
                  <div className="space-y-3">
                    {analysis?.deliveryModeAlternatives?.map((alt, idx) => (
                      <div key={idx} className="bg-card border border-border rounded-lg p-4">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <h5 className="text-sm font-semibold text-foreground capitalize">{alt?.mode}</h5>
                          <span className="text-xs font-medium text-success">{alt?.estimatedSavings}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{alt?.rationale}</p>
                        <div className="flex items-start gap-2 text-xs text-muted-foreground">
                          <Icon name="Info" size={14} />
                          <span><strong>Tradeoffs:</strong> {alt?.tradeoffs}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Resource Optimization */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Zap" size={18} color="var(--color-primary)" />
                  <h4 className="text-sm font-semibold text-foreground">Resource Optimization</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {analysis?.resourceOptimization?.map((opt, idx) => (
                    <div key={idx} className="bg-card border border-border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon name={categoryIcons?.[opt?.category]} size={16} color="var(--color-primary)" />
                        <h5 className="text-xs font-semibold text-foreground capitalize">{opt?.category}</h5>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{opt?.recommendation}</p>
                      <div className="text-xs text-success font-medium">
                        <Icon name="ArrowRight" size={12} className="inline mr-1" />
                        {opt?.impact}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 space-y-3">
              <Icon name="AlertCircle" size={48} color="var(--color-muted-foreground)" />
              <p className="text-sm text-muted-foreground">No analysis available</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIRecommendationsModal;